import React, { useEffect, useMemo, useRef, useState } from "react";
import { isOpeningSessionJob } from "../../utils/isOpeningSessionJob";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";

/** Rotating copy from the former Opening Session modelling wait. */
const MODEL_WORKING_BEATS = [
  "Reading names, roles, and how things connect…",
  "Matching known cloud and technology products…",
  "Drafting neighbours, hosting, and flows…",
  "Parking gaps instead of inventing systems…",
  "Still working — the extract often takes a few minutes…",
];

function isActiveEnrichmentJob(job) {
  if (!job || isOpeningSessionJob(job)) return false;
  const st = String(job?.status || "").toLowerCase();
  return !["completed", "failed", "cancelled"].includes(st);
}

function useProcessingElapsed(active, jobs) {
  const [elapsedSec, setElapsedSec] = useState(0);
  const startedAtRef = useRef(null);

  useEffect(() => {
    if (!active) {
      startedAtRef.current = null;
      setElapsedSec(0);
      return undefined;
    }

    if (startedAtRef.current == null) {
      let start = Date.now();
      const times = (jobs || [])
        .filter(isActiveEnrichmentJob)
        .map((j) => new Date(j.created_at || j.updated_at || 0).getTime())
        .filter((t) => Number.isFinite(t) && t > 0);
      if (times.length) start = Math.min(...times);
      startedAtRef.current = start;
    }

    const tick = () => {
      const started = startedAtRef.current || Date.now();
      setElapsedSec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, jobs]);

  return elapsedSec;
}

/**
 * Cyan header strip while enrichment jobs run — rotating beats + live seconds.
 */
export default function EnrichmentProcessingBanner() {
  const { hasActiveEnrichmentProcessing, enrichmentJobs } =
    useV2EnrichmentReview();
  const active = Boolean(hasActiveEnrichmentProcessing);
  const elapsedSec = useProcessingElapsed(active, enrichmentJobs);

  const beat = useMemo(() => {
    if (!active) return "";
    const idx = Math.floor(elapsedSec / 4) % MODEL_WORKING_BEATS.length;
    return MODEL_WORKING_BEATS[idx];
  }, [active, elapsedSec]);

  if (!active) return null;

  return (
    <div
      className="px-6 py-2.5 border-t border-cyan-200/90 bg-cyan-50 text-sm text-cyan-950 flex flex-wrap items-center gap-x-3 gap-y-1.5"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-50" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-600" />
      </span>
      <span className="font-medium">
        Building your map
        {elapsedSec > 0 ? (
          <span className="font-normal text-cyan-900/80"> · {elapsedSec}s</span>
        ) : null}
      </span>
      <span className="text-cyan-900/80 text-xs sm:text-sm min-w-0 flex-1">
        {beat}
      </span>
      <span className="text-cyan-900/65 text-xs shrink-0">
        Check Inbox when it is ready.
      </span>
    </div>
  );
}
